import { computed, ref } from 'vue';
import type {
  OnboardingChecklist,
  OnboardingChecklistItem,
  OnboardingChecklistComment,
} from '@/types/onboarding-checklist';
import { defaultOnboardingChecklist } from '@/lib/onboarding-checklist-default';
import { dateToDateOnlyString } from '@lib/formatters';
import { useUserConfig } from './user-config';
import { useUser } from './user';
import { useRouter } from 'vue-router';
import { useArticleStatuses } from './article-statuses';
import { useMChartStatuses } from './mchart-statuses';
import { useMChartTags } from './mchart-tags';
import { useComponentStatuses } from './component-statuses';
import { useComponentAttributeTypes } from './component-attribute-types';
import { useComponentTypes } from './component-types';
import { useComponentTags } from './component-tags';
import { useOrderStatuses } from './order-statuses';
import { useOrderTypes } from './order-types';
import { useOrderTags } from './order-tags';
import { useCurrencies } from './currencies';
import { useSeasons } from './seasons';
import { useHierarchy } from './hierarchy';
import { useHierarchyLevels } from './hierarchy-levels';
import { useProductCategories } from './product-categories';
import { useSizeSystems } from './size-systems';
import { useSuppliers } from './suppliers';
import { useSupplierStatuses } from './supplier-statuses';
import { useUnits } from './units';
import { useMeasurements } from './measurements';
import { useTags } from './tags';
import { useTreatments } from './treatments';
import { useCertificates } from './certificates';
import { useCompliances } from './compliances';
import { useMaterials } from './materials';
import { useMaterialDetailTypes } from './material-detail-types';
import { useColors } from './colors';
import { useColorGroups } from './color-groups';
import { useDeliveryTerms } from './delivery-terms';
import { usePaymentTerms } from './payment-terms';
import { useModeOfTransports } from './mode-of-transports';
import { useSampleRoundTypes } from './sample-round-types';

const CHECKLIST_KEY = 'onboarding-checklist';

// The backend's sync-item-task endpoint reconciles a per-item ChatMessage to the
// item's desired state (last-writer-wins) and dedups on a per-item key
// (`onboarding-checklist:{itemId}`). Two facts follow from that:
//   1. Concurrent requests for the SAME item race on the unique dedup-key index
//      with no backend guard, so the loser 500s — we must never have two of them
//      in flight at once.
//   2. Only the latest desired state matters, so intermediate edits to the same
//      item are redundant and can be coalesced away.
// Requests for DIFFERENT items can never collide (different keys), so we key
// everything by itemId and let separate items sync in parallel. Module-level (not
// per-composable) so it coalesces across every component instance on the page.
const itemSyncChains = new Map<string, Promise<void>>();
const itemPendingSync = new Map<string, () => Promise<void>>();

export function enqueueSync(itemId: string, task: () => Promise<void>): Promise<void> {
  // Keep only the latest desired state for this item; a newer edit supersedes any
  // edit still queued behind the in-flight request.
  itemPendingSync.set(itemId, task);

  let chain = itemSyncChains.get(itemId);
  if (!chain) {
    chain = drainItemSync(itemId);
    itemSyncChains.set(itemId, chain);
  }
  return chain;
}

async function drainItemSync(itemId: string): Promise<void> {
  try {
    for (;;) {
      const next = itemPendingSync.get(itemId);
      if (!next) return;
      itemPendingSync.delete(itemId);
      // Swallow per-request failures so one bad sync doesn't strand later edits
      // for this item; the next edit re-asserts desired state anyway.
      await next().catch(() => {});
    }
  } finally {
    itemSyncChains.delete(itemId);
  }
}

export function useOnboardingChecklist() {
  const userConfig = useUserConfig();
  const router = useRouter();

  function parseChecklist(savedChecklist: string | undefined): OnboardingChecklist {
    if (savedChecklist) {
      try {
        return JSON.parse(savedChecklist);
      } catch {
        // If parsing fails, use default
        return JSON.parse(JSON.stringify(defaultOnboardingChecklist));
      }
    }

    // No saved checklist, use default
    return JSON.parse(JSON.stringify(defaultOnboardingChecklist));
  }

  function getChecklist(): OnboardingChecklist {
    return parseChecklist(userConfig.get(CHECKLIST_KEY));
  }

  // The whole checklist is one config value, so an edit made while an earlier one is still
  // saving must apply to what that save leaves, not to the checklist as it was read before it.
  // The store runs `edit` when this write's turn comes.
  async function editChecklist(edit: (checklist: OnboardingChecklist) => void) {
    await userConfig.set(
      CHECKLIST_KEY,
      (saved: string | undefined) => {
        const checklist = parseChecklist(saved);
        edit(checklist);
        return JSON.stringify(checklist);
      },
      'brand'
    );
  }

  // The backend deadline is a DateOnly, so it only accepts "yyyy-MM-dd". Older
  // items (and the previous add-item default) stored a full ISO datetime, which
  // fails model binding with a 400. Normalize to a date-only string before sync.
  function toDeadlineOnly(deadline?: string): string | undefined {
    if (!deadline) return undefined;
    if (/^\d{4}-\d{2}-\d{2}$/.test(deadline)) return deadline;
    const parsed = new Date(deadline);
    return isNaN(parsed.getTime()) ? undefined : dateToDateOnlyString(parsed);
  }

  function syncItemTask(item: OnboardingChecklistItem, removed: boolean) {
    const user = useUser();
    const brandId = user.brandId;
    if (!brandId) return Promise.resolve();

    let contextUrl: string | undefined;
    let contextLabel: string | undefined;
    if (item.adminPageRoute) {
      const routeName = item.adminPageRoute.startsWith('admin-') ? item.adminPageRoute : `admin-${item.adminPageRoute}`;
      if (router?.hasRoute(routeName)) {
        contextUrl = router.resolve({ name: routeName }).href;
        contextLabel = item.title;
      }
    }

    return enqueueSync(item.id, () =>
      user.$api.onboardingChecklist_SyncItemTask(brandId, {
        itemId: item.id,
        title: item.title,
        description: item.description,
        contextUrl,
        contextLabel,
        responsibleUserId: item.responsibleUserId,
        deadline: toDeadlineOnly(item.deadline),
        completed: item.completed,
        removed,
      })
    );
  }

  function calculateItemStatus(item: OnboardingChecklistItem): { currentValue: number; isComplete: boolean } {
    let currentValue = 0;
    let isComplete = false;

    switch (item.calculationType) {
      case 'manual':
        isComplete = item.completed;
        break;
      case 'article-statuses-count':
        currentValue = useArticleStatuses().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'mchart-statuses-count':
        currentValue = useMChartStatuses().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'component-statuses-count':
        currentValue = useComponentStatuses().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'component-attribute-types-count':
        currentValue = useComponentAttributeTypes().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'order-statuses-count':
        currentValue = useOrderStatuses().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'currencies-count':
        currentValue = useCurrencies().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'seasons-count':
        currentValue = useSeasons().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'hierarchy-nodes-count':
        currentValue = useHierarchy().fullNodes?.length ?? 0;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'product-categories-count':
        currentValue = useProductCategories().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'size-systems-count':
        currentValue = useSizeSystems().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'suppliers-count':
        currentValue = useSuppliers().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'units-count':
        currentValue = useUnits().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'mchart-tags-count':
        currentValue = useMChartTags().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'component-types-count':
        currentValue = useComponentTypes().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'component-tags-count':
        currentValue = useComponentTags().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'order-types-count':
        currentValue = useOrderTypes().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'order-tags-count':
        currentValue = useOrderTags().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'hierarchy-levels-count':
        currentValue = useHierarchyLevels().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'supplier-statuses-count':
        currentValue = useSupplierStatuses().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'measurements-count':
        currentValue = useMeasurements().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'tags-count':
        currentValue = useTags().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'treatments-count':
        currentValue = useTreatments().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'certificates-count':
        currentValue = useCertificates().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'compliances-count':
        currentValue = useCompliances().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'materials-count':
        currentValue = useMaterials().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'material-detail-types-count':
        currentValue = useMaterialDetailTypes().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'colors-count':
        currentValue = useColors().palettes.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'color-groups-count':
        currentValue = useColorGroups().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'delivery-terms-count':
        currentValue = useDeliveryTerms().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'payment-terms-count':
        currentValue = usePaymentTerms().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'mode-of-transports-count':
        currentValue = useModeOfTransports().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
      case 'sample-round-types-count':
        currentValue = useSampleRoundTypes().loaded.length;
        isComplete = currentValue >= (item.targetValue ?? 1);
        break;
    }

    return { currentValue, isComplete };
  }

  const checklist = computed(() => getChecklist());

  const checklistWithStatus = computed(() => {
    const current = checklist.value;
    if (!current) return null;

    return {
      ...current,
      groups: current.groups.map((group) => ({
        ...group,
        items: group.items.map((item) => {
          const { currentValue, isComplete } = calculateItemStatus(item);
          return {
            ...item,
            currentValue,
            isComplete,
          };
        }),
      })),
    };
  });

  const totalItems = computed(() => {
    return checklist.value.groups.reduce((sum, group) => sum + group.items.length, 0);
  });

  const completedItems = computed(() => {
    const current = checklistWithStatus.value;
    if (!current) return 0;
    return current.groups.reduce((sum, group) => sum + group.items.filter((item) => item.completed).length, 0);
  });

  const progressPercentage = computed(() => {
    if (totalItems.value === 0) return 0;
    return Math.round((completedItems.value / totalItems.value) * 100);
  });

  async function toggleItemCompleted(groupId: string, itemId: string) {
    let toggled: OnboardingChecklistItem | undefined;

    await editChecklist((current) => {
      const group = current.groups.find((g) => g.id === groupId);
      if (!group) return;

      const item = group.items.find((i) => i.id === itemId);
      if (!item) return;

      item.completed = !item.completed;

      // Track completion timestamp
      if (item.completed) {
        item.completedAt = new Date().toISOString();
      } else {
        item.completedAt = undefined;
      }

      toggled = item;
    });

    if (toggled) await syncItemTask(toggled, false);
  }

  async function addCustomItem(
    groupId: string,
    title: string,
    description?: string,
    calculationType: OnboardingChecklistItem['calculationType'] = 'manual',
    targetValue?: number,
    adminPageRoute?: string,
    responsibleUserId?: string
  ) {
    // Default deadline: one week from now
    const defaultDeadline = new Date();
    defaultDeadline.setDate(defaultDeadline.getDate() + 7);

    let added: OnboardingChecklistItem | undefined;

    await editChecklist((current) => {
      const group = current.groups.find((g) => g.id === groupId);
      if (!group) return;

      const newItem: OnboardingChecklistItem = {
        id: `custom-${Date.now()}`,
        title,
        description,
        order: group.items.length,
        completed: false,
        calculationType,
        targetValue,
        adminPageRoute,
        responsibleUserId,
        deadline: dateToDateOnlyString(defaultDeadline),
      };

      group.items.push(newItem);
      added = newItem;
    });

    if (added) await syncItemTask(added, false);
  }

  async function removeItem(groupId: string, itemId: string) {
    let removedItem: OnboardingChecklistItem | undefined;

    await editChecklist((current) => {
      const group = current.groups.find((g) => g.id === groupId);
      if (!group) return;

      removedItem = group.items.find((i) => i.id === itemId);
      group.items = group.items.filter((i) => i.id !== itemId);
    });

    if (removedItem) await syncItemTask(removedItem, true);
  }

  async function updateItem(groupId: string, itemId: string, updates: Partial<OnboardingChecklistItem>) {
    let updated: OnboardingChecklistItem | undefined;

    await editChecklist((current) => {
      const group = current.groups.find((g) => g.id === groupId);
      if (!group) return;

      const item = group.items.find((i) => i.id === itemId);
      if (!item) return;

      Object.assign(item, updates);
      updated = item;
    });

    if (updated) await syncItemTask(updated, false);
  }

  async function updateItemOrder(groupId: string, items: OnboardingChecklistItem[]) {
    const order = new Map(items.map((item, index) => [item.id, index]));

    await editChecklist((current) => {
      const group = current.groups.find((g) => g.id === groupId);
      if (!group) return;

      group.items = group.items
        .map((item) => ({ ...item, order: order.get(item.id) ?? items.length }))
        .sort((a, b) => a.order - b.order);
    });
  }

  async function updateGroupOrder(groups: OnboardingChecklist['groups']) {
    const order = new Map(groups.map((group, index) => [group.id, index]));

    await editChecklist((current) => {
      current.groups = current.groups
        .map((group) => ({ ...group, order: order.get(group.id) ?? groups.length }))
        .sort((a, b) => a.order - b.order);
    });
  }

  async function replaceChecklist(next: OnboardingChecklist) {
    let previous: OnboardingChecklist | undefined;

    await editChecklist((current) => {
      previous = JSON.parse(JSON.stringify(current));
      current.groups = next.groups;
    });

    if (previous) for (const group of previous.groups) for (const item of group.items) await syncItemTask(item, true);
  }

  async function resetToDefault() {
    await replaceChecklist(JSON.parse(JSON.stringify(defaultOnboardingChecklist)));
  }

  async function removeGroup(groupId: string) {
    let removed: OnboardingChecklist['groups'][0] | undefined;

    await editChecklist((current) => {
      removed = current.groups.find((g) => g.id === groupId);
      current.groups = current.groups.filter((g) => g.id !== groupId);
    });

    if (removed) for (const item of removed.items) await syncItemTask(item, true);
  }

  async function clearChecklist() {
    await replaceChecklist({ groups: [] });
  }

  async function addCustomGroup(title: string, description?: string) {
    await editChecklist((current) => {
      const newGroup: OnboardingChecklist['groups'][0] = {
        id: `custom-group-${Date.now()}`,
        title,
        description,
        order: current.groups.length,
        items: [],
      };

      current.groups.push(newGroup);
    });
  }

  async function addComment(groupId: string, itemId: string, text: string, userId: string) {
    if (!text.trim()) return;

    const newComment: OnboardingChecklistComment = {
      id: `comment-${Date.now()}`,
      text: text.trim(),
      userId,
      timestamp: Date.now(),
    };

    await editChecklist((current) => {
      const group = current.groups.find((g) => g.id === groupId);
      if (!group) return;

      const item = group.items.find((i) => i.id === itemId);
      if (!item) return;

      if (!item.comments) {
        item.comments = [];
      }

      item.comments.unshift(newComment); // Add to beginning (latest first)
    });
  }

  async function removeComment(groupId: string, itemId: string, commentId: string) {
    await editChecklist((current) => {
      const group = current.groups.find((g) => g.id === groupId);
      if (!group) return;

      const item = group.items.find((i) => i.id === itemId);
      if (!item || !item.comments) return;

      const commentIndex = item.comments.findIndex((c) => c.id === commentId);
      if (commentIndex === -1) return;

      item.comments.splice(commentIndex, 1);
    });
  }

  async function moveItemBetweenGroups(fromGroupId: string, itemId: string, toGroupId: string) {
    await editChecklist((current) => {
      const fromGroup = current.groups.find((g) => g.id === fromGroupId);
      const toGroup = current.groups.find((g) => g.id === toGroupId);

      if (!fromGroup || !toGroup) return;

      const itemIndex = fromGroup.items.findIndex((i) => i.id === itemId);
      if (itemIndex === -1) return;

      const [item] = fromGroup.items.splice(itemIndex, 1);
      toGroup.items.push(item);
    });
  }

  async function updateGroup(
    groupId: string,
    updates: Partial<Omit<OnboardingChecklist['groups'][0], 'id' | 'items'>>
  ) {
    await editChecklist((current) => {
      const group = current.groups.find((g) => g.id === groupId);
      if (!group) return;

      Object.assign(group, updates);
    });
  }

  return {
    checklist,
    checklistWithStatus,
    totalItems,
    completedItems,
    progressPercentage,
    toggleItemCompleted,
    addCustomItem,
    addCustomGroup,
    removeItem,
    removeGroup,
    updateItem,
    updateItemOrder,
    updateGroup,
    updateGroupOrder,
    moveItemBetweenGroups,
    addComment,
    removeComment,
    resetToDefault,
    clearChecklist,
  };
}
